// User session and firm management store
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { authClient, login as authLogin, logout as authLogout } from '@/auth/client';
import { trpc } from '@/trpc';
import { clearAllQueryClients } from '@/trpc/queryClients';
import { scopeRef, setScope } from '@/trpc/scope';
import { logger } from '@/utils/logger';
import { useSearchStore } from './searchStore';
import type { CurrencyScale } from '@/utils/format';

/** A firm the current user has read access to. */
export interface Firm { id: string; name: string; currencyScale: CurrencyScale; trackUsTaxAttributes: boolean; showGlossaryLinks: boolean; }
export interface FirmPermission {
  firmId: string;
  canRead: boolean;
  canWrite: boolean;
  isAdmin: boolean;
  canReadPii: boolean;
}

export const useMainStore = defineStore('main', () => {
  ///////////////////////////////
  // state
  const loggedIn = ref(false);
  /**
   * True when the authenticated session has an active impersonation. While
   * impersonating, every user-facing read (`users.getCurrent`, firm pickers,
   * permissions) reflects the impersonated user — only the warning banner
   * tells the operator they are acting on someone else's behalf.
   */
  const isImpersonating = ref(false);
  /** Email of the currently effective user — under impersonation, the impersonated user. */
  const currentUserEmail = ref<string | null>(null);
  /** Display name of the currently effective user. */
  const currentUserName = ref<string | null>(null);
  /** Auth ID of the currently effective user. */
  const currentUserId = ref<string | null>(null);

  const availableFirms = ref<Firm[]>([]);
  const isGlobalAdmin = ref(false);

  /** Full per-firm permission rows for the current user; populated from users.getCurrent. */
  const permissions = ref<FirmPermission[]>([]);

  /**
   * Timestamp of the last successful session verification (ms). Zero = never
   * checked.
   *
   * `refreshUser` runs from the router's
   * `beforeEach` guard, i.e. on every single in-app navigation. Without this
   * throttle, every click fires a real network round trip (Better Auth
   * session check + `users.getCurrent`) before the route resolves — a
   * previous attempt to remove it for "freshness" (picking up a cross-tab
   * logout/impersonation change sooner) made rapid navigation noticeably
   * slower and made the app far more exposed to `loadUser`'s catch-all
   * forced-logout on a transient network/backend blip, since that check now
   * ran on every navigation instead of at most once per TTL window.
   * `startImpersonation`/`stopImpersonation` already bypass this via
   * `force=true`, which is the correct way to get immediate freshness for a
   * known session change — extend that pattern rather than removing the cache.
   */
  const lastRefreshedAt = ref(0);
  const SESSION_CACHE_TTL_MS = 30_000;

  ///////////////////////////////
  // actions

  /**
   * Restores the session from the Better Auth cookie, then loads the user's
   * firm list from the tRPC backend.
   *
   * Skips the network call when the session was verified within
   * SESSION_CACHE_TTL_MS and the user is already logged in — covers rapid
   * in-app navigation where the session cannot have changed. Pass force=true
   * to bypass the cache (used by impersonation start/stop, which are the
   * known-stale-session cases that need immediate freshness).
   */
  const refreshUser = async (force = false): Promise<void> => {
    const now = Date.now();

    if (!force && loggedIn.value && now - lastRefreshedAt.value < SESSION_CACHE_TTL_MS)
      return;

    const { data } = await authClient.getSession();
    loggedIn.value = Boolean(data?.user);
    isImpersonating.value = Boolean((data?.session as { actingUid?: string | null } | undefined)?.actingUid);

    if (loggedIn.value) {
      await loadUser();
      // loadUser resets the session (loggedIn = false) on failure, so only stamp
      // the cache timestamp when the session actually held up.
      if (loggedIn.value)
        lastRefreshedAt.value = now;
    } else {
      resetSession();
    }
  };

  /** Sign in with email + password via Better Auth. */
  const login = async (email: string, password: string): Promise<void> => {
    await authLogin(email, password);
    loggedIn.value = true;
    await loadUser();

    // The active scope is written by the router once it confirms a `/:firmId` route;
    // login only has to leave the persisted preference alone for the router to read.
  };

  /** Sign out the current user. */
  const logout = async (): Promise<void> => {
    await authLogout();
    resetSession();
    setScope({ firmId: '', asOf: null });
    useSearchStore().reset();
  };

  ///////////////////////////////
  // internal

  /*
   * DELIBERATE RAW STORE READ. Every other store read moved to a scoped `useQuery` in a
   * composable; this one stays a raw `trpc` call inside the store on purpose. It loads
   * the session user, their available firms and their permissions —
   * not firm-scoped data, and the result is what decides which scope is even valid, so it
   * cannot live under a scope's QueryClient. It is refreshed by the router's `beforeEach`
   * on every authenticated navigation.
   */
  const loadUser = async (): Promise<void> => {
    try {
      const data = await trpc.users.getCurrent.query();
      if (!data) {
        availableFirms.value = [];
        isGlobalAdmin.value = false;
        permissions.value = [];
        currentUserEmail.value = null;
        currentUserName.value = null;
        currentUserId.value = null;
        return;
      }
      availableFirms.value = data.availableFirms;
      isGlobalAdmin.value = data.isGlobalAdmin;
      permissions.value = data.permissions;
      currentUserEmail.value = data.email;
      currentUserName.value = data.name;
      currentUserId.value = data.uid;
    } catch (err) {
      logger.error('Failed to load user data:', err);
      resetSession();
      await useRouter()?.push('/login');
    }
  };

  const resetSession = (): void => {
    loggedIn.value = false;
    availableFirms.value = [];
    isGlobalAdmin.value = false;
    permissions.value = [];
    isImpersonating.value = false;
    currentUserEmail.value = null;
    currentUserName.value = null;
    currentUserId.value = null;
    lastRefreshedAt.value = 0;
    clearAllQueryClients();
  };

  /**
   * Begin acting as another user. After the backend updates the auth session,
   * we tear down every scoped query cache and refresh the session so the rest of the
   * app picks up the impersonated user's permissions/email immediately.
   *
   * A firm switch no longer clears anything: each scope keeps its own cache, so a
   * firm's data stays in browser memory for up to gcTime (30 min) after switching away.
   * That is deliberate — it is the same authenticated user, holding a permissions row on
   * both firms, and RLS still governs every read. Impersonation is the one case that
   * must still drop everything immediately: the effective user is not a scope dimension,
   * and a payload decrypted under an admin's canReadPii must never surface in an
   * impersonated context. Both call sites follow with a window.location.reload(), but
   * this store action has to be correct on its own.
   */
  const startImpersonation = async (
    targetEmail: string,
    reason?: string,
  ): Promise<void> => {
    await trpc.admin.startImpersonation.mutate({ targetEmail, reason });
    clearAllQueryClients();
    setScope({ firmId: '', asOf: null });
    await refreshUser(true);
  };

  /**
   * End the active impersonation and return to the admin's own context.
   * Same cache-clear + session refresh as startImpersonation.
   */
  const stopImpersonation = async (): Promise<void> => {
    await trpc.admin.stopImpersonation.mutate();
    clearAllQueryClients();
    setScope({ firmId: '', asOf: null });
    await refreshUser(true);
  };

  ///////////////////////////////
  // computed
  const currentFirmId = computed((): string => scopeRef.value.firmId);

  /** Whether the current user is a firm admin on the currently-active firm */
  const isAdminForCurrentFirm = computed((): boolean => {
    if (isGlobalAdmin.value) return true;
    const fid = currentFirmId.value;
    if (!fid) return false;
    return permissions.value.some((p) => p.firmId === fid && p.isAdmin);
  });

  /** Whether the current user has write access to the currently-active firm */
  const canWriteForCurrentFirm = computed((): boolean => {
    if (isGlobalAdmin.value) return true;
    const fid = currentFirmId.value;
    if (!fid) return false;
    return permissions.value.some((p) => p.firmId === fid && p.canWrite);
  });

  /** Whether the current user can view PII fields on the currently-active firm */
  const canReadPiiForCurrentFirm = computed((): boolean => {
    if (isGlobalAdmin.value) return true;
    const fid = currentFirmId.value;
    if (!fid) return false;
    return permissions.value.some((p) => p.firmId === fid && p.canReadPii);
  });

  /** Currency scale setting for the currently-active firm */
  const currentFirmCurrencyScale = computed((): CurrencyScale => {
    const fid = currentFirmId.value;
    return availableFirms.value.find((f) => f.id === fid)?.currencyScale ?? 'millions';
  });

  /**
   * Whether the currently-active firm tracks US tax attributes (SCR-21 §6.5).
   * Fails open: defaults to true while the firm is unresolved so a slow store
   * never hides data that should be visible.
   */
  const currentFirmTracksUsTax = computed((): boolean => {
    const fid = currentFirmId.value;
    return availableFirms.value.find((f) => f.id === fid)?.trackUsTaxAttributes ?? true;
  });

  /**
   * Whether the currently-active firm underlines glossary terms in the UI. Defaults to
   * true while the firm is unresolved, matching the column default.
   */
  const currentFirmShowsGlossaryLinks = computed((): boolean => {
    const fid = currentFirmId.value;
    return availableFirms.value.find((f) => f.id === fid)?.showGlossaryLinks ?? true;
  });

  ///////////////////////////////
  // public interface
  return {
    currentFirmId,
    availableFirms,
    loggedIn,
    isGlobalAdmin,
    isAdminForCurrentFirm,
    canWriteForCurrentFirm,
    canReadPiiForCurrentFirm,
    currentFirmCurrencyScale,
    currentFirmTracksUsTax,
    currentFirmShowsGlossaryLinks,
    permissions,
    isImpersonating,
    currentUserEmail,
    currentUserName,
    currentUserId,
    refreshUser,
    login,
    logout,
    startImpersonation,
    stopImpersonation,
  };
});
